/**
 * TranscriptionEvents - Centralized event management for transcription lifecycle
 */
export class TranscriptionEvents {
  static EVENTS = {
    // Permission events
    PERMISSION_REQUESTED: 'permission:requested',
    PERMISSION_GRANTED: 'permission:granted',
    PERMISSION_DENIED: 'permission:denied',

    // Transcription events
    TRANSCRIPTION_STARTED: 'transcription:started',
    TRANSCRIPTION_ENDED: 'transcription:ended',
    TRANSCRIPTION_PROCESSING: 'transcription:processing', // User starts speaking
    TRANSCRIPTION_PROCESSED: 'transcription:processed', // User is done speaking and we have a transcription for what was spoken
    TRANSCRIPTION_UPDATE: 'transcription:update',
    TRANSCRIPTION_ERROR: 'transcription:error',

    // Connection events
    CONNECTION_CONNECTING: 'connection:connecting',
    CONNECTION_ESTABLISHED: 'connection:established',
    CONNECTION_LOST: 'connection:lost',
  }

  constructor() {
    this.listeners = new Map()
  }

  /**
   * Add an event listener
   * @param {string} event Event name
   * @param {Function} callback Callback function
   */
  on(event, callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function')
    }

    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }

    this.listeners.get(event).add(callback)
  }

  /**
   * Remove an event listener
   * @param {string} event Event name
   * @param {Function} callback Callback function to remove
   */
  off(event, callback) {
    if (!this.listeners.has(event)) return

    this.listeners.get(event).delete(callback)

    // Clean up empty event sets
    if (this.listeners.get(event).size === 0) {
      this.listeners.delete(event)
    }
  }

  /**
   * Emit an event to all listeners
   * @param {string} event Event name
   * @param {*} data Data to pass to listeners
   */
  emit(event, data = null) {
    if (!this.listeners.has(event)) return

    const callbacks = this.listeners.get(event)

    // Create a copy of the callbacks set to avoid issues if listeners are modified during iteration
    const callbacksCopy = Array.from(callbacks)

    callbacksCopy.forEach((callback) => {
      try {
        callback(data)
      } catch (error) {
        console.error(`Error in event listener for ${event}:`, error)
      }
    })
  }

  /**
   * Add a one-time event listener that removes itself after being called
   * @param {string} event Event name
   * @param {Function} callback Callback function
   */
  once(event, callback) {
    const onceCallback = (data) => {
      callback(data)
      this.off(event, onceCallback)
    }

    this.on(event, onceCallback)
  }

  /**
   * Remove all listeners for a specific event, or all listeners if no event specified
   * @param {string} [event] Event name (optional)
   */
  removeAllListeners(event = null) {
    if (event) {
      this.listeners.delete(event)
    } else {
      this.listeners.clear()
    }
  }

  /**
   * Check if there are any listeners for an event
   * @param {string} event Event name
   * @returns {boolean}
   */
  hasListeners(event) {
    return this.listeners.has(event) && this.listeners.get(event).size > 0
  }

  /**
   * Get the number of listeners for an event
   * @param {string} event Event name
   * @returns {number}
   */
  getListenerCount(event) {
    return this.listeners.has(event) ? this.listeners.get(event).size : 0
  }

  /**
   * Get all event names that have listeners
   * @returns {Array<string>}
   */
  getEventNames() {
    return Array.from(this.listeners.keys())
  }

  /**
   * Create a promise that resolves when a specific event is emitted
   * @param {string} event Event name
   * @param {number} [timeout] Optional timeout in milliseconds
   * @returns {Promise}
   */
  waitFor(event, timeout = null) {
    return new Promise((resolve, reject) => {
      let timeoutId = null

      const callback = (data) => {
        if (timeoutId) clearTimeout(timeoutId)
        resolve(data)
      }

      this.once(event, callback)

      if (timeout) {
        timeoutId = setTimeout(() => {
          this.off(event, callback)
          reject(new Error(`Timeout waiting for event: ${event}`))
        }, timeout)
      }
    })
  }

  /**
   * Create a promise that resolves when any of the specified events is emitted
   * @param {Array<string>} events Array of event names
   * @param {number} [timeout] Optional timeout in milliseconds
   * @returns {Promise<{event: string, data: *}>}
   */
  waitForAny(events, timeout = null) {
    return new Promise((resolve, reject) => {
      let timeoutId = null
      let resolved = false

      const callbacks = events.map((event) => {
        const callback = (data) => {
          if (resolved) return
          resolved = true

          if (timeoutId) clearTimeout(timeoutId)

          // Remove all callbacks
          events.forEach((evt, index) => {
            this.off(evt, callbacks[index])
          })

          resolve({ event, data })
        }

        this.once(event, callback)
        return callback
      })

      if (timeout) {
        timeoutId = setTimeout(() => {
          if (resolved) return
          resolved = true

          // Remove all callbacks
          events.forEach((event, index) => {
            this.off(event, callbacks[index])
          })

          reject(new Error(`Timeout waiting for events: ${events.join(', ')}`))
        }, timeout)
      }
    })
  }
}